import { Directive, ElementRef, Input, OnDestroy, OnInit, ViewRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import { IScrollToOptions } from '@df/dom/scroll/scroll-to.service';
import { ScrollToErrorService } from '@df/ui/form/scroll-to-error.service';
import { UiComponent } from '@df/ui/ui.component';
import { Subject, Subscription } from 'rxjs';

@Directive()
export abstract class CheckoutStepComponentDef extends UiComponent implements OnInit, OnDestroy, Checkout.IStep {
    /**
     * Step id (used in code and GTM)
     */
    @Input({
        required: true
    })
    name!: Checkout.EStepName;

    /**
     * Step title (used on FE)
     */
    @Input({
        required: true
    })
    stepTitle!: string;

    /**
     * Step order param if needed
     */
    @Input({
        required: true
    })
    order!: number;

    forms: NgForm[] = [];

    private formSubscriptions = new Map<NgForm, Subscription>();

    submittedSubject = new Subject<boolean>();

    /**
     * If step is valid flag
     */
    private _isValid = false;

    validityChangeSubject = new Subject();

    constructor(
        protected checkoutFormComponent: CheckoutFormComponentDef,
        protected ngEl: ElementRef,
        readonly app: App,
        protected logger: Logger,
        protected scrollToErrorService: ScrollToErrorService
    ) {
        super();
    }

    /**
     * On init check
     */
    override ngOnInit() {
        if (!this.name || !this.order) {
            this.logger.error(`Checkout step requires both name [${this.name}] and order [${this.order}] attributes`);
        }
        this.checkoutFormComponent.registerStep(this as Checkout.IStep);
        this.subscriptions.add(this.checkoutFormComponent.currentStepName$.subscribe(() => this.detectChanges()));
        this.check();
        super.ngOnInit();
    }

    override ngOnDestroy() {
        this.checkoutFormComponent.unregisterStep(this as Checkout.IStep);
        this.formSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.unsubscribe();
        super.ngOnDestroy();
    }

    private check(): boolean {
        const valid = this.isValid();
        if (this._isValid !== valid) {
            this._isValid = valid;

            const eventData: Checkout.IStepValidityChangeData = {
                step: this as Checkout.IStep
            };

            if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                this.timeoutService.setTimeout(() => {
                    this.app.events.broadcast<Checkout.IStepValidityChangeData>(Checkout.EVENTS.StepValidityChange, eventData);
                    this.validityChangeSubject.next(this.valid);
                });
            }
        }

        return this.valid;
    }

    get valid(): boolean {
        return this._isValid;
    }

    get domElement(): HTMLElement {
        return this.ngEl.nativeElement;
    }

    get isConfirmationLock(): boolean {
        return !!this.checkoutFormComponent.confirmationLock;
    }

    markSubmitted(scroll = true): void {
        this.forms.forEach(form => {
            form.submitAttempt = true;
            form.ngSubmit.emit();
        });
        this.submittedSubject.next(true);
        this.check();
        this.detectChanges();
        if (scroll && !this.valid) {
            this.timeoutService.setTimeout(() => {
                this.scrollToError();
            }, 100);
        }
    }

    scrollToError(): void {
        this.scrollToErrorService.scrollTo(this.domElement, this.getToErrorScrollOptions());
    }

    protected getToErrorScrollOptions(): IScrollToOptions {
        return {};
    }

    markUnsubmitted(): void {
        this.forms.forEach(form => (form.submitAttempt = false));
        this.submittedSubject.next(false);
    }

    addForm(form: NgForm) {
        if (form && !this.forms.includes(form)) {
            this.forms.push(form);
            if (form.valueChanges) {
                const subscription = new Subscription();
                let initialChange: any = {};
                subscription.add(form.statusChanges?.subscribe(change => this.check()));
                subscription.add(
                    form.valueChanges.subscribe(change => {
                        if (!form.pristine) {
                            this.timeoutService.setTimeout(() => {
                                this.checkoutFormComponent.onFormDataChange(this.name, change);
                            }, 0);
                        } else {
                            Object.assign(initialChange, change);
                            this.verifyInitialChanges(initialChange);
                        }
                    })
                );
                this.formSubscriptions.set(form, subscription);
            }
            this.check();
        }
    }

    private verifyInitialChanges = this.timeoutService.debounce((initialChange: any) => {
        this.checkoutFormComponent.onFormDataChange(this.name, initialChange);
    }, 500);

    removeForm(form?: NgForm) {
        if (form) {
            const index = this.forms.indexOf(form);
            if (index > -1) {
                this.forms.splice(index, 1);
            }
            const subscription = this.formSubscriptions.get(form);
            if (subscription) {
                subscription.unsubscribe();
            }
            this.formSubscriptions.delete(form);
            this.check();
        }
    }

    get nextStep(): Checkout.IStep | undefined {
        return this.checkoutFormComponent.getNextStep(this as Checkout.IStep);
    }

    get isCurrent(): boolean {
        const step = this.currentStep;
        return !!(step && this.name === step.name);
    }

    get currentStep(): Checkout.IStep | undefined {
        return this.checkoutFormComponent.currentStep;
    }

    goTo(): void {
        this.checkoutFormComponent.goToStep(this as Checkout.IStep);
    }

    canEdit() {
        return !this.checkoutFormComponent.stepsSequence.find(step => step.order < this.order && !step.valid);
    }

    protected isValid(): boolean {
        if (this.name === 'delivery' && this.checkoutFormComponent.saveShippingAddressStatus.error) {
            return false;
        }

        return this.areFormsValid();
    }

    protected areFormsValid(): boolean {
        return !this.forms
            .map(form => {
                form.ngSubmit.emit();
                return form.valid;
            })
            .includes(false);
    }
}
